import da from './da.json';
import en from './en.json';
import de from './de.json';

export type Translations = typeof da;
export type Locale = 'da' | 'en' | 'de' | 'vola';
export type Currency = 'dkk' | 'eur';

function toVolaText(input: string): string {
  const placeholders: string[] = [];
  const protectedInput = input.replace(/\{[^}]+\}/g, (token) => {
    placeholders.push(token);
    return `@@${placeholders.length - 1}@@`;
  });

  function chefifyWord(word: string): string {
    let out = word.toLowerCase();

    const rules: Array<[RegExp, string]> = [
      [/tion/g, 'shun'],
      [/ing\b/g, 'ing-a'],
      [/ow/g, 'oo'],
      [/an/g, 'un'],
      [/au/g, 'oo'],
      [/o/g, 'oo'],
      [/u/g, 'oo'],
      [/i/g, 'ee'],
      [/v/g, 'f'],
      [/w/g, 'v'],
      [/th/g, 'd'],
      [/f/g, 'ff'],
      [/e\b/g, 'e-a'],
      [/en\b/g, 'ee'],
      [/er\b/g, 'ur'],
    ];

    for (const [pattern, replacement] of rules) {
      out = out.replace(pattern, replacement);
    }

    if (out.length <= 2) {
      out = `${out}e`;
    }

    if (word === word.toUpperCase()) return out.toUpperCase();
    if (word[0] === word[0]?.toUpperCase()) return out[0].toUpperCase() + out.slice(1);
    return out;
  }

  let result = protectedInput.replace(/[A-Za-zÆØÅæøå]+/g, (word) => chefifyWord(word));

  const targetLength = Math.max(14, Math.floor(protectedInput.length * 0.78));
  if (result.length > targetLength) {
    const words = result.split(/\s+/);
    const reduced = words.filter((word, idx) => {
      if (!word) return false;
      if (word.startsWith('@@') && word.endsWith('@@')) return true;
      if (idx === 0) return true;
      return idx % 4 !== 0;
    });
    result = reduced.join(' ');
  }

  result = result
    .replace(/!/g, '! Bork bork!')
    .replace(/\?/g, '? Bork?')
    .replace(/,/g, ' , ')
    .replace(/\s{2,}/g, ' ');

  return result.replace(/@@(\d+)@@/g, (_, idx) => placeholders[Number(idx)] ?? '');
}

function toVolaValue<T>(value: T): T {
  if (typeof value === 'string') {
    return toVolaText(value) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => toVolaValue(item)) as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, innerValue] of Object.entries(value as Record<string, unknown>)) {
      out[key] = toVolaValue(innerValue);
    }
    return out as T;
  }
  return value;
}

function buildVolaTranslations(base: Translations): Translations {
  const vola = toVolaValue(base);
  vola._meta = {
    ...vola._meta,
    locale: 'vola',
    language: 'Bork Bork Dansk',
  };
  vola.metadata = {
    ...vola.metadata,
    ogLocale: 'da_DK',
  };
  return vola;
}

const vola = buildVolaTranslations(da);

export const allLocales: Locale[] = ['da', 'en', 'de', 'vola'];
export const defaultTranslations: Record<Locale, Translations> = { da, en, de, vola };

export const localeCurrency: Record<Locale, Currency> = {
  da: 'dkk',
  en: 'eur',
  de: 'eur',
  vola: 'dkk',
};

export const localeNames: Record<Locale, string> = {
  da: 'Dansk',
  en: 'English',
  de: 'Deutsch',
  vola: 'Klonish',
};

export const localeFlags: Record<Locale, string> = {
  da: '🇩🇰',
  en: '🇬🇧',
  de: '🇩🇪',
  vola: '🏴‍☠️',
};

export function getTranslations(locale: Locale): Translations {
  return defaultTranslations[locale] ?? da;
}

export function detectLocale(availableLocales: Locale[] = allLocales): Locale {
  if (typeof window === 'undefined') return 'da';

  const stored = localStorage.getItem('klonkaffe-locale');
  if (stored && availableLocales.includes(stored as Locale)) {
    return stored as Locale;
  }

  const browserLangs = navigator.languages ?? [navigator.language];
  for (const lang of browserLangs) {
    const code = lang.split('-')[0].toLowerCase();
    if (availableLocales.includes(code as Locale)) {
      return code as Locale;
    }
  }

  return availableLocales[0] ?? 'da';
}

const t = da;
export default t;
